import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SignOutButton } from '../../components/SignOutButton';
import { readClaims, useSession } from '../../hooks/useSession';
import { fetchWithAuth } from '../../lib/accessToken';

/**
 * Super-admin omniscient pricing-predictions view — Feature 5.
 *
 * Cross-tenant list of recent pricing predictions. Unlike the tenant
 * card, super_admin sees full provenance: model name, prompt template
 * version, token counts. Hits IE Worker at /v1/dashboard/predictions.
 */

interface ConfidenceBand {
  hammerLowCents: number;
  hammerMidCents: number;
  hammerHighCents: number;
  sellThroughProbability: number;
  modelConfidence: number;
}

interface PredictionRow {
  productId: string;
  tenantId: string;
  lotId: string;
  modelName: string;
  promptTemplateName: string;
  promptTemplateVersion: number;
  band: ConfidenceBand;
  rationale: string;
  generatedAt: string;
  cacheTtlExpiresAt: string;
  inputTokens: number;
  outputTokens: number;
}

interface ListSnapshot {
  builtAtMs: number;
  totalPredictions: number;
  predictions: PredictionRow[];
}

interface ApiError {
  error: string;
  detail: string;
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; data: ListSnapshot }
  | { kind: 'error'; status: number; message: string };

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - Date.parse(iso);
  if (Number.isNaN(diff)) return '';
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export function SuperPricingPredictions() {
  const { session, userId } = useSession();
  const { appRole } = readClaims(session);
  const [tenantFilter, setTenantFilter] = useState('');
  const [limit, setLimit] = useState(50);
  const [state, setState] = useState<FetchState>({ kind: 'idle' });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = ieBaseUrl();
    if (!base) {
      setState({
        kind: 'error',
        status: 0,
        message: 'VITE_INTELLIGENCE_BASE_URL is not configured.',
      });
      return;
    }
    if (userId === null) {
      setState({ kind: 'error', status: 0, message: 'No active session.' });
      return;
    }
    setState({ kind: 'loading' });
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (tenantFilter.trim().length > 0) params.set('tenant_id', tenantFilter.trim());
    const url = `${base}/v1/dashboard/predictions?${params.toString()}`;
    fetchWithAuth(url)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          setState({
            kind: 'error',
            status: res.status,
            message: body?.detail ?? `HTTP ${res.status}`,
          });
          return;
        }
        const data = (await res.json()) as ListSnapshot;
        setState({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'unknown error';
        setState({ kind: 'error', status: 0, message });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantFilter, limit, refreshNonce, userId]);

  return (
    <main style={{ padding: '2rem', maxWidth: '90rem', margin: '0 auto' }}>
      <div
        style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Pricing predictions
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Signed in as {session?.user.email ?? 'unknown'} ·{' '}
            <Badge variant="outline">{appRole ?? 'no role'}</Badge> ·{' '}
            <Link to="/super/dashboard" style={{ color: 'var(--brand-primary)' }}>
              ← Super dashboard
            </Link>
          </p>
        </div>
        <SignOutButton redirectTo="/super" />
      </div>

      <div
        style={{
          display: 'flex',
          gap: '1rem',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Tenant filter:</span>
          <input
            type="text"
            placeholder="all tenants"
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Limit:</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={inputStyle}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </label>
        <button
          onClick={() => setRefreshNonce((n) => n + 1)}
          type="button"
          disabled={state.kind === 'loading'}
          style={{ ...buttonStyle, marginLeft: 'auto' }}
        >
          {state.kind === 'loading' ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {state.kind === 'idle' || state.kind === 'loading' ? (
        <Card>
          <CardContent>
            <p style={{ color: 'var(--text-muted)' }}>
              {state.kind === 'idle' ? 'Initializing…' : 'Loading predictions…'}
            </p>
          </CardContent>
        </Card>
      ) : state.kind === 'error' ? (
        <Card>
          <CardHeader>
            <CardTitle>Predictions unavailable</CardTitle>
            <CardDescription>HTTP {state.status || 'no-network'}</CardDescription>
          </CardHeader>
          <CardContent>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                color: 'var(--semantic-danger)',
                fontFamily: 'var(--font-mono, ui-monospace)',
                fontSize: '0.85rem',
                margin: 0,
              }}
            >
              {state.message}
            </pre>
          </CardContent>
        </Card>
      ) : (
        <PredictionsTable data={state.data} />
      )}
    </main>
  );
}

function PredictionsTable({ data }: { data: ListSnapshot }) {
  if (data.predictions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No predictions yet</CardTitle>
          <CardDescription>
            Predictions are generated on-demand when tenants view a lot detail page or click Refresh
            on the AI prediction card. None have been generated yet.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.85rem',
            }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <Th>Tenant</Th>
                <Th>Lot</Th>
                <Th align="right">Low</Th>
                <Th align="right">Likely</Th>
                <Th align="right">High</Th>
                <Th align="right">ST%</Th>
                <Th align="right">Conf</Th>
                <Th>Model</Th>
                <Th>Template</Th>
                <Th align="right">Tokens</Th>
                <Th>Generated</Th>
              </tr>
            </thead>
            <tbody>
              {data.predictions.map((p, i) => (
                <PredictionRowView key={`${p.tenantId}::${p.lotId}::${i}`} p={p} />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function PredictionRowView({ p }: { p: PredictionRow }) {
  const totalTokens = p.inputTokens + p.outputTokens;
  return (
    <tr style={{ borderBottom: '1px solid var(--border-subtle, var(--border))' }}>
      <Td>
        <code style={{ fontSize: '0.75rem' }}>{p.tenantId.slice(0, 8)}…</code>
      </Td>
      <Td>
        <code style={{ fontSize: '0.75rem' }}>{p.lotId.slice(0, 8)}…</code>
      </Td>
      <Td align="right">{formatCents(p.band.hammerLowCents)}</Td>
      <Td align="right">
        <strong>{formatCents(p.band.hammerMidCents)}</strong>
      </Td>
      <Td align="right">{formatCents(p.band.hammerHighCents)}</Td>
      <Td align="right">{Math.round(p.band.sellThroughProbability * 100)}%</Td>
      <Td align="right">{p.band.modelConfidence.toFixed(2)}</Td>
      <Td>
        <code style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{p.modelName}</code>
      </Td>
      <Td>
        <code style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {p.promptTemplateName} v{p.promptTemplateVersion}
        </code>
      </Td>
      <Td align="right">
        <span style={{ color: 'var(--text-muted)' }}>{totalTokens}</span>
      </Td>
      <Td>{formatRelativeTime(p.generatedAt)}</Td>
    </tr>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: '0.5rem 0.75rem',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <td style={{ textAlign: align, padding: '0.5rem 0.75rem', verticalAlign: 'top' }}>
      {children}
    </td>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderRadius: '0.375rem',
  border: '1px solid var(--border)',
  background: 'var(--surface-elev)',
  color: 'var(--text)',
  fontSize: '0.85rem',
};

const buttonStyle: React.CSSProperties = {
  padding: '0.4rem 0.9rem',
  borderRadius: '0.4rem',
  border: '1px solid var(--border)',
  background: 'var(--surface-elev)',
  color: 'var(--text)',
  fontWeight: 600,
  fontSize: '0.85rem',
  cursor: 'pointer',
};
